import type {
  AttentionItem,
  AttentionKind,
  Attacker,
  MetricSeries,
  OverviewHour,
  OverviewResponse,
} from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { getOverview } from '../api/overview.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ActionDialog } from '../components/ActionDialog.js';
import { AddressLink, AddressStatusBadge } from '../components/AddressStatus.js';
import { Flag } from '../components/Flag.js';
import { ThreatMapCard } from '../components/ThreatMapCard.js';
import { MetricChart } from '../components/MetricChart.js';
import { PageHeader } from '../components/PageHeader.js';
import { SeverityBadge } from '../components/StatusBadge.js';
import { SystemHealthCard } from '../components/SystemHealthCard.js';
import { formatPercent } from '../lib/format.js';
import { effectiveTimezone, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { Loading } from '../ui/Skeleton.js';
import {
  ActivityIcon,
  BanIcon,
  HandIcon,
  ServerIcon,
  ServerOffIcon,
  ShieldIcon,
} from '../ui/icons.js';
import { StatIcon } from '../ui/StatIcon.js';

/**
 * The landing page as a decision centre (P11.2).
 *
 * It answers, in order: is anything wrong; what do I decide first, and where; who
 * is attacking and is each one stopped; how did the last day go. Every row says
 * where its decision is made, and the one decision that can be made here — banning
 * an attacker nothing stops — has its button beside it, through the usual preview.
 *
 * One request (`GET /overview`), refreshed every minute while the page is open.
 */

const REFRESH_MS = 60_000;

/** The verb on each row: what the person does on the page it opens. */
const ACTION_LABEL: Record<AttentionKind, string> = {
  event: 'Review',
  attacker: 'Decide',
  suggestion: 'Approve or reject',
  settings_failed: 'See why',
  module_unhealthy: 'Check',
  wazuh_agent: 'Check',
  vulnerability: 'Update',
  server_offline: 'Check',
  server_pending: 'Approve',
};

/** Each kind counted in words: one and many. */
const KIND_LABEL: Record<AttentionKind, [string, string]> = {
  // An alert is the events of one server, category and address, as the list groups them.
  event: ['unread high alert', 'unread high alerts'],
  attacker: ['attacker not stopped', 'attackers not stopped'],
  suggestion: ['AI suggestion waiting', 'AI suggestions waiting'],
  settings_failed: ['settings version refused', 'settings versions refused'],
  module_unhealthy: ['module unwell', 'modules unwell'],
  wazuh_agent: ['Wazuh agent unreachable', 'Wazuh agents unreachable'],
  vulnerability: ['server with urgent fixes', 'servers with urgent fixes'],
  server_offline: ['server offline', 'servers offline'],
  server_pending: ['waiting for approval', 'waiting for approval'],
};

const kindCount = (kind: AttentionKind, n: number): string =>
  `${n.toLocaleString('en-US')} ${KIND_LABEL[kind][n === 1 ? 0 : 1]}`;

const count = (n: number): string => n.toLocaleString('en-US');

const pointsOf = (hours: OverviewHour[], value: (hour: OverviewHour) => number) =>
  hours.map((hour) => {
    const each = value(hour);
    return { at: hour.at, avg: each, min: each, max: each, samples: 1 };
  });

function eventLines(hours: OverviewHour[]): MetricSeries[] {
  const all = (hour: OverviewHour): number =>
    Object.values(hour.events).reduce((sum, n) => sum + n, 0);
  return [
    {
      name: 'events_count',
      labels: { line: 'All events' },
      points: pointsOf(hours, all),
      trend: null,
    },
    {
      name: 'events_count',
      labels: { line: 'High and critical' },
      points: pointsOf(hours, (hour) => (hour.events.high ?? 0) + (hour.events.critical ?? 0)),
      trend: null,
    },
  ];
}

const requestLines = (hours: OverviewHour[]): MetricSeries[] => [
  {
    name: 'web_requests_count',
    labels: { line: 'Requests' },
    points: pointsOf(hours, (hour) => hour.requests),
    trend: null,
  },
];

export function OverviewPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const canAct = session.user.role !== 'viewer';
  const [banning, setBanning] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
    refetchInterval: REFRESH_MS,
  });

  return (
    <div className="page">
      <PageHeader
        title="Overview"
        description="What needs your decision, who is attacking and whether each is stopped, and how the last day went."
      />

      {!session.user.totpEnabled && (
        <Alert tone="warning" title="Your account has no second factor">
          Anyone who learns your password can reach this whole fleet.{' '}
          <Link to="/account">Set up an authenticator app</Link>.
        </Alert>
      )}

      {query.isPending && <Loading />}
      {query.isError && (
        <Alert tone="danger" title="Could not load the overview">
          Try again in a moment.
        </Alert>
      )}
      {query.data !== undefined && (
        <Decisions
          data={query.data}
          timezone={timezone}
          canAct={canAct}
          onBan={(ip) => {
            setBanning(ip);
          }}
        />
      )}

      {session.user.role === 'admin' && <SystemHealthCard timezone={timezone} />}

      {banning !== null && (
        <ActionDialog
          type="ban_ip"
          initialIp={banning}
          onClose={() => {
            setBanning(null);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function Decisions({
  data,
  timezone,
  canAct,
  onBan,
}: {
  data: OverviewResponse;
  timezone: string;
  canAct: boolean;
  onBan: (ip: string) => void;
}): ReactNode {
  const navigate = useNavigate();
  const waiting = Object.values(data.attentionCounts).reduce((sum, n) => sum + n, 0);
  const servers = data.fleet.active + data.fleet.offline;
  return (
    <>
      <dl className="stat-tiles">
        <div className={`stat-tile ${data.fleet.offline > 0 ? 'stat-tile-alert' : ''}`}>
          <StatIcon
            icon={data.fleet.offline > 0 ? ServerOffIcon : ServerIcon}
            tone={data.fleet.offline > 0 ? 'danger' : 'ok'}
          />
          <dt>Servers watched</dt>
          <dd>
            {count(data.fleet.active)}
            <span className="stat-of"> of {count(servers)}</span>
          </dd>
          <p>
            {data.fleet.offline > 0
              ? `${count(data.fleet.offline)} offline`
              : data.fleet.pending > 0
                ? `${count(data.fleet.pending)} waiting for approval`
                : 'All reporting'}
          </p>
          <Link to="/servers" className="stat-link">
            View all →
          </Link>
        </div>
        <div className={`stat-tile ${waiting > 0 ? 'stat-tile-alert' : ''}`}>
          <StatIcon icon={HandIcon} tone={waiting > 0 ? 'danger' : 'ok'} />
          <dt>Needs a decision</dt>
          <dd>{count(waiting)}</dd>
          <p>{waiting === 0 ? 'Nothing waiting' : 'See the list below'}</p>
        </div>
        <div className="stat-tile">
          <StatIcon icon={ShieldIcon} tone={data.totals.highEvents > 0 ? 'danger' : 'accent'} />
          <dt>Events, last 24 h</dt>
          <dd>{count(data.totals.events)}</dd>
          <p>{count(data.totals.highEvents)} high or critical</p>
          <Link to="/events?min_severity=high" className="stat-link">
            View all →
          </Link>
        </div>
        <div className="stat-tile">
          <StatIcon icon={ActivityIcon} />
          <dt>Requests, last 24 h</dt>
          <dd>{count(data.totals.requests)}</dd>
          <p>To the websites Bartizo watches</p>
          <Link to="/traffic" className="stat-link">
            View all →
          </Link>
        </div>
        <div className="stat-tile">
          <StatIcon icon={BanIcon} tone="ok" />
          <dt>Addresses stopped now</dt>
          <dd>{count(data.totals.stoppedNow)}</dd>
          <p>By CrowdSec, Bartizo or a person</p>
          <Link to="/blocklist" className="stat-link">
            View all →
          </Link>
        </div>
      </dl>

      <Card
        title="Needs your decision"
        aside={
          <Link to="/events" className="text-sm">
            View all events →
          </Link>
        }
        {...(waiting === 0
          ? {}
          : {
              description: Object.entries(data.attentionCounts)
                .map(([kind, n]) => kindCount(kind as AttentionKind, n))
                .join(' · '),
            })}
      >
        {data.attention.length === 0 ? (
          <p className="muted">
            Nothing needs you right now. Every server is reporting and nothing is waiting.
          </p>
        ) : (
          <ul className="attention-list">
            {data.attention.map((item) => (
              <AttentionRow key={`${item.kind}-${item.link}-${item.title}`} item={item} />
            ))}
          </ul>
        )}
      </Card>

      <ThreatMapCard />

      <Card
        title="Top attackers, last 24 hours"
        aside={
          <Link to="/blocklist" className="text-sm">
            View blocklist →
          </Link>
        }
        description="From their events, and from probing: many requests, most of them refused, which is what scanners leave behind."
      >
        {data.attackers.length === 0 ? (
          <p className="muted">No address has attacked or probed in the last day.</p>
        ) : (
          <DataTable
            caption="Top attackers, last 24 hours"
            columns={attackerColumns(canAct, onBan)}
            rows={data.attackers}
            rowKey={(attacker) => attacker.ip}
            onSelect={(attacker) => {
              void navigate(`/addresses/${encodeURIComponent(attacker.ip)}`);
            }}
          />
        )}
      </Card>

      <div className="traffic-grid">
        <Card
          title="Events per hour"
          aside={
            <Link to="/events" className="text-sm">
              View all events →
            </Link>
          }
        >
          <MetricChart
            title="Events per hour"
            series={eventLines(data.hours)}
            from={data.hours[0]?.at ?? data.generatedAt}
            to={data.generatedAt}
            bucketSeconds={3600}
            format={count}
            seriesLabel={(line) => line.labels['line'] ?? ''}
            yMin={0}
            timezone={timezone}
            emptyText="No events in the last day."
          />
        </Card>
        <Card
          title="Requests per hour"
          aside={
            <Link to="/traffic" className="text-sm">
              Open Traffic →
            </Link>
          }
        >
          <MetricChart
            title="Requests per hour"
            series={requestLines(data.hours)}
            from={data.hours[0]?.at ?? data.generatedAt}
            to={data.generatedAt}
            bucketSeconds={3600}
            format={count}
            seriesLabel={(line) => line.labels['line'] ?? ''}
            yMin={0}
            timezone={timezone}
            emptyText="No traffic in the last day. A server counts it once its web environment is confirmed."
          />
        </Card>
      </div>
    </>
  );
}

function AttentionRow({ item }: { item: AttentionItem }): ReactNode {
  return (
    <li className="attention-item">
      <SeverityBadge severity={item.severity} />
      <div className="attention-body">
        <Link to={item.link} className="attention-title">
          {item.title}
        </Link>
        {item.detail !== null && <span className="cell-sub">{item.detail}</span>}
      </div>
      <span className="attention-when muted text-sm">
        {item.at === null ? '' : formatRelative(item.at)}
      </span>
      <Link to={item.link} className="button button-secondary attention-go">
        {ACTION_LABEL[item.kind]}
      </Link>
    </li>
  );
}

/**
 * One line on why an address is on the list, from the same counts the API used
 * to put it there: its events, its refused requests, how many servers saw it,
 * and how far a ban already reaches.
 */
function whyFlagged(attacker: Attacker): string {
  const parts: string[] = [];
  if (attacker.events > 0) {
    parts.push(
      `${count(attacker.events)} ${attacker.kind === 'probing' ? '' : `${attacker.kind} `}event${attacker.events === 1 ? '' : 's'}`,
    );
  }
  if (attacker.requests > 0 && attacker.refused > 0) {
    parts.push(
      `${formatPercent((attacker.refused / attacker.requests) * 100, 0)} of ${count(attacker.requests)} requests refused, as a scanner's are`,
    );
  }
  parts.push(`seen on ${count(attacker.servers)} server${attacker.servers === 1 ? '' : 's'}`);
  if (attacker.status === 'partly') {
    parts.push(`banned on ${count(attacker.bannedOn)} of them`);
  }
  return parts.join(' · ');
}

/** Not stopped first, then the busiest: the order a person acts in. */
const looseness = (attacker: Attacker): number =>
  (attacker.status === 'open' ? 2 : attacker.status === 'partly' ? 1 : 0) * 1e9 +
  attacker.events +
  attacker.refused;

function attackerColumns(canAct: boolean, onBan: (ip: string) => void): Column<Attacker>[] {
  return [
    {
      key: 'ip',
      header: 'Address',
      rowHeader: true,
      cell: (attacker) => (
        <>
          <AddressLink ip={attacker.ip} />
          <span className="cell-sub">
            <Flag code={attacker.country} name />
            {attacker.network !== null && ` · ${attacker.network}`}
          </span>
        </>
      ),
    },
    {
      key: 'what',
      header: 'What it did',
      sortValue: (attacker) => attacker.events + attacker.refused,
      cell: (attacker) => (
        <>
          {attacker.kind === 'probing' ? 'Probing' : attacker.kind}
          <span className="cell-sub">Why flagged: {whyFlagged(attacker)}</span>
        </>
      ),
    },
    {
      key: 'refused',
      header: '% refused',
      align: 'end',
      sortValue: (attacker) => (attacker.requests === 0 ? 0 : attacker.refused / attacker.requests),
      cell: (attacker) =>
        attacker.requests === 0 ? (
          <span className="muted" title="No requests to a website Bartizo watches">
            —
          </span>
        ) : (
          <span title={`${count(attacker.refused)} of ${count(attacker.requests)} requests`}>
            {formatPercent((attacker.refused / attacker.requests) * 100, 0)}
          </span>
        ),
    },
    {
      key: 'seen',
      header: 'Last seen',
      wideOnly: true,
      sortValue: (attacker) => Date.parse(attacker.lastSeenAt),
      cell: (attacker) => formatRelative(attacker.lastSeenAt),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: looseness,
      cell: (attacker) => <AddressStatusBadge status={attacker.status} />,
    },
    ...(canAct
      ? [
          {
            key: 'act',
            header: 'Next',
            cell: (attacker: Attacker) =>
              attacker.status === 'open' || attacker.status === 'partly' ? (
                <Button
                  variant="danger"
                  aria-label={`Ban ${attacker.ip}`}
                  onClick={() => {
                    onBan(attacker.ip);
                  }}
                >
                  Ban
                </Button>
              ) : null,
          },
        ]
      : []),
  ];
}
